import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download, RotateCcw, SlidersHorizontal } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { money } from "@/lib/api";
import { exportCsv, exportRows } from "@/lib/exporters";
import Pager from "@/components/Pager";

const PAGE_SIZE = 25;
const SORTS = [
  { id: "date-desc", label: "Date ↓" },
  { id: "date-asc", label: "Date ↑" },
  { id: "name", label: "Name A-Z" },
  { id: "clinic", label: "Clinic ↓" },
];

const NO_FILTERS = { patient: "", from: "", to: "", treatment: "", consultant: "", sort: "date-desc" };

export default function Records() {
  const { data, showMoney, isOwner } = useApp();
  const [f, setF] = useState(NO_FILTERS);
  const filtering = f.patient || f.from || f.to || f.treatment || f.consultant;
  const extraFilters = [f.from, f.to, f.treatment, f.consultant, f.sort !== NO_FILTERS.sort].filter(Boolean).length;
  const [moreOpen, setMoreOpen] = useState(false); // phones show only the name search until asked
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [f]);

  const nameOf = (id) => data.patients.find((p) => p.id === id)?.name || "—";
  const consultantOf = (id) => data.consultants.find((c) => c.id === id)?.name || "—";

  const filtered = useMemo(() => {
    const t = f.patient.trim().toLowerCase();
    let rows = data.visits.filter((v) => {
      if (t && !nameOf(v.patient_id).toLowerCase().includes(t)) return false;
      if (f.from && (v.date || "") < f.from) return false;
      if (f.to && (v.date || "") > f.to) return false;
      if (f.treatment && v.treatment !== f.treatment) return false;
      if (f.consultant && v.consultant_id !== f.consultant) return false;
      return true;
    });
    const cmp = {
      "date-desc": (a, b) => (b.date || "").localeCompare(a.date || ""),
      "date-asc": (a, b) => (a.date || "").localeCompare(b.date || ""),
      name: (a, b) => nameOf(a.patient_id).localeCompare(nameOf(b.patient_id)),
      clinic: (a, b) => Number(b.clinic_share || 0) - Number(a.clinic_share || 0),
    }[f.sort];
    return [...rows].sort(cmp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.visits, data.patients, f]);

  const shape = (list) =>
    list.map((v, i) => ({
      "#": i + 1, Date: v.date, Patient: nameOf(v.patient_id), Diagnosis: v.diagnosis || "",
      Treatment: v.treatment || "", Consultant: consultantOf(v.consultant_id),
      ...(showMoney ? { "Clinic Share": v.clinic_share ?? 0, "Consultant Fee": v.consultant_fee ?? 0, "Lab Cost": v.lab_cost ?? 0 } : {}),
      Remarks: v.remarks || "",
    }));

  const treatments = useMemo(() => [...new Set(data.visits.map((v) => v.treatment).filter(Boolean))].sort(), [data.visits]);
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="space-y-5" data-testid="records-page">
      <div>
        <h1 className="dm-page-title">
          All <span className="font-extrabold">records</span>{" "}
          <span className="font-mono text-lg text-slate-500" data-testid="rec-count">{filtered.length}</span>
        </h1>
      </div>

      <div className="dm-panel overflow-hidden">
        <div className="grid gap-3 border-b border-slate-100 p-4 sm:grid-cols-3 lg:grid-cols-6" data-testid="rec-filters">
          <div className="flex items-end gap-2 sm:block">
            <div className="flex-1">
              <p className="label-xs mb-1">Patient</p>
              <input data-testid="rec-patient" className="dm-input" placeholder="Name…" value={f.patient} onChange={set("patient")} />
            </div>
            <button type="button" data-testid="rec-more-filters" onClick={() => setMoreOpen(!moreOpen)} aria-expanded={moreOpen} className="dm-btn-ghost !px-3 sm:hidden">
              <SlidersHorizontal size={14} /> Filters{extraFilters ? ` (${extraFilters})` : ""}
            </button>
          </div>
          <div className={moreOpen ? "contents" : "hidden sm:contents"}>
          <div>
            <p className="label-xs mb-1">From</p>
            <input type="date" data-testid="rec-from" className="dm-input" value={f.from} onChange={set("from")} />
          </div>
          <div>
            <p className="label-xs mb-1">To</p>
            <input type="date" data-testid="rec-to" className="dm-input" value={f.to} onChange={set("to")} />
          </div>
          <div>
            <p className="label-xs mb-1">Treatment</p>
            <select data-testid="rec-treatment" className="dm-input" value={f.treatment} onChange={set("treatment")}>
              <option value="">All treatments</option>
              {treatments.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <p className="label-xs mb-1">Consultant</p>
            <select data-testid="rec-consultant" className="dm-input" value={f.consultant} onChange={set("consultant")}>
              <option value="">All consultants</option>
              {data.consultants.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <p className="label-xs mb-1">Sort</p>
            <select data-testid="rec-sort" className="dm-input" value={f.sort} onChange={set("sort")}>
              {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-2">
          <div className="flex items-center gap-2 text-[13px] text-slate-500">
            <span><span className="font-semibold text-slate-800">{filtered.length.toLocaleString("en-IN")}</span> visit{filtered.length === 1 ? "" : "s"}</span>
            {filtering && <button data-testid="rec-clear" onClick={() => setF(NO_FILTERS)} className="dm-btn-quiet !px-2 !py-1 !text-xs"><RotateCcw size={12} /> Clear filters</button>}
          </div>
          {isOwner && (
            <div className="flex flex-wrap items-center gap-1.5">
              <button data-testid="rec-csv-filtered" onClick={() => exportCsv(shape(filtered), "records-filtered")} className="dm-btn-ghost !px-3 !py-1 !text-xs"><Download size={13} /> Export CSV</button>
              <button data-testid="rec-excel-filtered" onClick={() => exportRows(shape(filtered), "records-filtered", "Records")} className="dm-btn-ghost !px-3 !py-1 !text-xs"><Download size={13} /> Export Excel</button>
              {filtering && (
                <>
                  <span className="ml-1 text-xs text-slate-400">All records:</span>
                  <button data-testid="rec-csv-all" onClick={() => exportCsv(shape(data.visits), "records-all")} className="dm-btn-quiet !px-2 !py-1 !text-xs">CSV</button>
                  <button data-testid="rec-excel-all" onClick={() => exportRows(shape(data.visits), "records-all", "Records")} className="dm-btn-quiet !px-2 !py-1 !text-xs">Excel</button>
                </>
              )}
            </div>
          )}
        </div>
        {filtered.length === 0 ? (
          <div className="dm-empty m-4" data-testid="rec-empty">No visits match these filters.</div>
        ) : (
          <div className="max-h-[68vh] overflow-auto">
            <table className="dm-table" data-testid="rec-table">
              <thead>
                <tr>
                  <th>#</th><th>Date</th><th>Patient</th><th>Diagnosis</th><th>Treatment</th><th>Consultant</th>
                  <th className="!text-right">Clinic Share</th><th className="!text-right">Consultant Fee</th><th className="!text-right">Lab Cost</th><th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((v, i) => (
                  <tr key={v.id} data-testid={`rec-row-${v.id}`}>
                    <td className="font-mono text-slate-500">{(page - 1) * PAGE_SIZE + i + 1}</td>
                    <td className="whitespace-nowrap font-mono text-slate-600">{v.date}</td>
                    <td><Link to={`/app/patients/${v.patient_id}`} className="font-semibold text-slate-800 hover:dm-accent-text">{nameOf(v.patient_id)}</Link></td>
                    <td>{v.diagnosis || "—"}</td>
                    <td>{v.treatment || "—"}</td>
                    <td>{consultantOf(v.consultant_id)}</td>
                    <td className="num text-slate-800">{showMoney ? money(v.clinic_share) : "•••"}</td>
                    <td className="num">{showMoney ? money(v.consultant_fee) : "•••"}</td>
                    <td className="num">{showMoney ? money(v.lab_cost) : "•••"}</td>
                    <td className="max-w-[220px] truncate text-slate-600" title={v.remarks || ""}>{v.remarks || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pager page={page} pageSize={PAGE_SIZE} total={filtered.length} onChange={setPage} testid="rec-pager" />
      </div>
    </div>
  );
}
